import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Loader } from 'lucide-react'

// Authentication
import { AuthProvider } from './contexts/AuthContext'
import { useAuth } from './contexts/AuthContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { SetupGuard } from './components/auth/SetupGuard'
import { TosGuard } from './components/auth/TosGuard'
import { AnalyticsTracker } from './components/AnalyticsTracker'

// Auth pages
import { SetupPage } from './pages/auth/SetupPage'
import { LoginPage } from './pages/auth/LoginPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'

// Layouts
import { AppLayout } from './layouts/AppLayout'
import { ProjectWorkflowLayout } from './layouts/ProjectWorkflowLayout'

// Lazy-loaded pages
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage'))
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'))
const DecorEditorDemoPage = React.lazy(() => import('./pages/DecorEditorDemoPage'))
const MediaLibraryPage = React.lazy(() => import('./pages/MediaLibraryPage'))
const CostDashboardPage = React.lazy(() => import('./pages/CostDashboardPage'))
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'))
const UploadPage = React.lazy(() => import('./pages/UploadPage'))
const AnalyzingPage = React.lazy(() => import('./pages/AnalyzingPage'))
const MaterialsPage = React.lazy(() => import('./pages/MaterialsPage'))
const TemplatePage = React.lazy(() => import('./pages/TemplatePage'))
const RenderPage = React.lazy(() => import('./pages/RenderPage'))
const StoryboardPage = React.lazy(() => import('./pages/StoryboardPage'))
const AdminLogsPage = React.lazy(() => import('./pages/AdminLogsPage'))
const AdminProjectsPage = React.lazy(() => import('./pages/AdminProjectsPage'))
const AdminStatsPage = React.lazy(() => import('./pages/AdminStatsPage'))
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'))
const AdminSystemLogsPage = React.lazy(() => import('./pages/AdminSystemLogsPage'))
const AdminPipelineFlowPage = React.lazy(() => import('./pages/AdminPipelineFlowPage'))
const AdminMediaPage = React.lazy(() => import('./pages/AdminMediaPage'))
const AdminCostPage = React.lazy(() => import('./pages/AdminCostPage'))
const AccountPage = React.lazy(() => import('./pages/AccountPage'))
const TransactionsPage = React.lazy(() => import('./pages/TransactionsPage'))
const AdminCampaignsPage = React.lazy(() => import('./pages/AdminCampaignsPage'))
const AdminBillingPage = React.lazy(() => import('./pages/AdminBillingPage'))
const AdminTipsPage = React.lazy(() => import('./pages/AdminTipsPage'))
const AdminPromptsPage = React.lazy(() => import('./pages/AdminPromptsPage'))
const AdminGlobalSettingsPage = React.lazy(() => import('./pages/AdminGlobalSettingsPage'))
const AdminRevenuePage = React.lazy(() => import('./pages/AdminRevenuePage'))
const AdminIpDuplicatesPage = React.lazy(() => import('./pages/AdminIpDuplicatesPage'))
const AdminChatbotSettingsPage = React.lazy(() => import('./pages/AdminChatbotSettingsPage'))
const AdminChatbotLogsPage = React.lazy(() => import('./pages/AdminChatbotLogsPage'))
const AdminTastePresetsPage = React.lazy(() => import('./pages/AdminTastePresetsPage'))
const AdminDecorPartsPage = React.lazy(() => import('./pages/AdminDecorPartsPage'))
const TermsOfUsePage = React.lazy(() => import('./pages/TermsOfUsePage'))
const SpecifiedCommercialPage = React.lazy(() => import('./pages/SpecifiedCommercialPage'))
const LandingPage = React.lazy(() => import('./pages/LandingPage'))
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage'))
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage'))
const PricingPage = React.lazy(() => import('./pages/PricingPage'))
const GenemoTopPage = React.lazy(() => import('./pages/GenemoTopPage'))
const TermsPage = React.lazy(() => import('./pages/TermsPage'))
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'))
const ManualPage = React.lazy(() => import('./pages/ManualPage'))

// Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <Loader className="w-8 h-8 animate-spin text-sky-400" />
  </div>
)

// Smart home: LP for guests, redirect to /projects for authenticated users
function SmartHome() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <PageLoader />
  return isAuthenticated ? <Navigate to="/projects" replace /> : (
    <Suspense fallback={<PageLoader />}><LandingPage /></Suspense>
  )
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
})

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AnalyticsTracker />
        <Routes>
          {/* Setup page (outside guard to avoid infinite redirect) */}
          <Route path="/setup" element={<SetupPage />} />

          {/* Fully public pages — no setup check, no auth required */}
          <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
          <Route path="/legal/specified-commercial" element={<Suspense fallback={<PageLoader />}><SpecifiedCommercialPage /></Suspense>} />

          {/* Setup Guard: セットアップ完了後のみアクセス可能 */}
          <Route element={<SetupGuard />}>
            {/* Public routes (no authentication required) */}
            <Route path="/" element={<SmartHome />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Suspense fallback={<PageLoader />}><SignupPage /></Suspense>} />
            <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
            <Route path="/genemo" element={<Suspense fallback={<PageLoader />}><GenemoTopPage /></Suspense>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<Suspense fallback={<PageLoader />}><VerifyEmailPage /></Suspense>} />

            {/* Protected routes (authentication required) */}
            <Route element={<ProtectedRoute />}>
              <Route element={<TosGuard />}>
                <Route element={<AppLayout />}>
                {/* Projects list */}
                <Route path="/projects" element={<Suspense fallback={<PageLoader />}><ProjectsPage /></Suspense>} />

                {/* New project */}
                <Route path="/new" element={<Suspense fallback={<PageLoader />}><UploadPage isNewProject /></Suspense>} />

                {/* Media library */}
                <Route path="/media" element={<Suspense fallback={<PageLoader />}><MediaLibraryPage /></Suspense>} />

                {/* Cost dashboard */}
                <Route path="/costs" element={<Suspense fallback={<PageLoader />}><CostDashboardPage /></Suspense>} />

                {/* History */}
                <Route path="/history" element={<Suspense fallback={<PageLoader />}><HistoryPage /></Suspense>} />

                {/* Settings */}
                <Route path="/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                <Route path="/decor-demo" element={<Suspense fallback={<PageLoader />}><DecorEditorDemoPage /></Suspense>} />

                {/* Admin: ログ調査 */}
                <Route path="/admin/logs" element={<Suspense fallback={<PageLoader />}><AdminLogsPage /></Suspense>} />

                {/* Admin: プロジェクト管理 */}
                <Route path="/admin/projects" element={<Suspense fallback={<PageLoader />}><AdminProjectsPage /></Suspense>} />

                {/* Admin: 統計ダッシュボード */}
                <Route path="/admin/stats" element={<Suspense fallback={<PageLoader />}><AdminStatsPage /></Suspense>} />

                {/* Admin: ユーザー管理 */}
                <Route path="/admin/users" element={<Suspense fallback={<PageLoader />}><AdminUsersPage /></Suspense>} />

                {/* Admin: IP重複管理 */}
                <Route path="/admin/ip-duplicates" element={<Suspense fallback={<PageLoader />}><AdminIpDuplicatesPage /></Suspense>} />

                {/* Admin: システムログ */}
                <Route path="/admin/system-logs" element={<Suspense fallback={<PageLoader />}><AdminSystemLogsPage /></Suspense>} />

                {/* Admin: パイプラインフロー */}
                <Route path="/admin/pipeline-flow" element={<Suspense fallback={<PageLoader />}><AdminPipelineFlowPage /></Suspense>} />

                {/* Admin: メディア管理 */}
                <Route path="/admin/media" element={<Suspense fallback={<PageLoader />}><AdminMediaPage /></Suspense>} />

                {/* Admin: コスト管理 */}
                <Route path="/admin/costs" element={<Suspense fallback={<PageLoader />}><AdminCostPage /></Suspense>} />

                {/* Admin: キャンペーン管理 */}
                <Route path="/admin/campaigns" element={<Suspense fallback={<PageLoader />}><AdminCampaignsPage /></Suspense>} />

                {/* Admin: 課金管理 */}
                <Route path="/admin/billing" element={<Suspense fallback={<PageLoader />}><AdminBillingPage /></Suspense>} />

                {/* Admin: 収益ダッシュボード */}
                <Route path="/admin/revenue" element={<Suspense fallback={<PageLoader />}><AdminRevenuePage /></Suspense>} />

                {/* Admin: チップス管理 */}
                <Route path="/admin/tips" element={<Suspense fallback={<PageLoader />}><AdminTipsPage /></Suspense>} />

                {/* Admin: テイスト管理 */}
                <Route path="/admin/taste-presets" element={<Suspense fallback={<PageLoader />}><AdminTastePresetsPage /></Suspense>} />
                <Route path="/admin/decor-parts" element={<Suspense fallback={<PageLoader />}><AdminDecorPartsPage /></Suspense>} />

                {/* Admin: プロンプト管理 */}
                <Route path="/admin/prompts" element={<Suspense fallback={<PageLoader />}><AdminPromptsPage /></Suspense>} />

                {/* Admin: グローバル設定 */}
                <Route path="/admin/global-settings" element={<Suspense fallback={<PageLoader />}><AdminGlobalSettingsPage /></Suspense>} />

                {/* Admin: チャットボット設定 */}
                <Route path="/admin/chatbot-settings" element={<Suspense fallback={<PageLoader />}><AdminChatbotSettingsPage /></Suspense>} />

                {/* Admin: チャットログ */}
                <Route path="/admin/chatbot-logs" element={<Suspense fallback={<PageLoader />}><AdminChatbotLogsPage /></Suspense>} />

                {/* アカウント・クレジット */}
                <Route path="/account" element={<Suspense fallback={<PageLoader />}><AccountPage /></Suspense>} />

                {/* 取引明細 */}
                <Route path="/transactions" element={<Suspense fallback={<PageLoader />}><TransactionsPage /></Suspense>} />

                {/* 使い方（アプリ全体マニュアル） */}
                <Route path="/manual" element={<Suspense fallback={<PageLoader />}><ManualPage /></Suspense>} />

                {/* 利用規約（アプリ内表示） */}
                <Route path="/terms-of-use" element={<Suspense fallback={<PageLoader />}><TermsOfUsePage /></Suspense>} />

                {/* Project workflow (nested layout) */}
                <Route path="/project/:projectId" element={<ProjectWorkflowLayout />}>
                  <Route index element={<Navigate to="upload" replace />} />
                  <Route path="upload" element={<Suspense fallback={<PageLoader />}><UploadPage /></Suspense>} />
                  <Route path="analyze" element={<Suspense fallback={<PageLoader />}><AnalyzingPage /></Suspense>} />
                  <Route path="materials" element={<Suspense fallback={<PageLoader />}><MaterialsPage /></Suspense>} />
                  <Route path="template" element={<Suspense fallback={<PageLoader />}><TemplatePage /></Suspense>} />
                  <Route path="render" element={<Suspense fallback={<PageLoader />}><RenderPage /></Suspense>} />
                  <Route path="storyboard" element={<Suspense fallback={<PageLoader />}><StoryboardPage /></Suspense>} />
                </Route>
              </Route>
              </Route>
            </Route>
          </Route>

          {/* Legacy route redirects */}
          <Route
            path="/project/:projectId/analyzing"
            element={<Navigate to="../analyze" replace />}
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
